import { useMemo, useState } from "react";
import { useParams } from "react-router";
import { Button, Table } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { ViewMode } from "@thepocman/gantt-task-react";
import TimelineLibrary from "@/components/Timeline/TimelineLibrary";
import TimelineHeaderControls from "@/components/Timeline/TimelineHeaderControls";
import { useTimelineControls } from "@/components/Timeline/useTimelineControls";
import { milestonesTimelineUtils } from "@/components/Timeline/milestonesTimelineUtils";
import type { TimelineItem } from "@/components/Timeline/timeline";
import { useTrialPlans } from "@/hooks/useTrialPlans";
import MilestoneEditor from "./components/MilestoneEditor";
import { activeSites } from "./plannedSites";
import type { PlanMilestone, SiteStartupDates } from "./types";

const EVENT_TYPE_BADGE: Record<string, string> = {
  "Planned Date": "bg-[#D8EEF8] text-[#196A9F]",
  Target: "bg-[#F8EEE1] text-[#C06119]",
  Forecast: "bg-[#CFE5D7] text-[#486563]",
  "Site Roll-up": "bg-[#E0DEF0] text-[#59528F]",
};

/** Site-level startup milestone categories rolled up into timeline bands. */
const SITE_MILESTONE_LABELS: Array<[keyof SiteStartupDates, string]> = [
  ["siteIdentification", "Site Identification"],
  ["feasibilitySurvey", "Feasibility Survey"],
  ["siteSelection", "Site Selection"],
  ["regulatorySubmission", "Regulatory Submission"],
  ["clinicalTrialAgreement", "Clinical Trial Agreement"],
  ["siteActivation", "Site Activation"],
  ["firstPatientIn", "First Patient In"],
  ["enrollmentComplete", "Enrollment Complete"],
  ["lastPatientLastVisit", "Last Patient Last Visit"],
];

const TIMELINE_COLUMNS = [
  { fieldName: "trial", visibility: "visible" as const },
  { fieldName: "event_type", visibility: "visible" as const },
  { fieldName: "event_name", visibility: "visible" as const },
  { fieldName: "start_date", visibility: "visible" as const },
  { fieldName: "end_date", visibility: "visible" as const },
];

export default function TrialPlanningMilestones() {
  const { planId } = useParams<{ planId: string }>();
  const { getPlan, updatePlan } = useTrialPlans();
  const plan = planId ? getPlan(planId) : undefined;
  const milestones = useMemo(() => plan?.milestones ?? [], [plan?.milestones]);
  const planName = plan?.name ?? "";

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingMilestone, setEditingMilestone] =
    useState<PlanMilestone | null>(null);
  const [editorMode, setEditorMode] = useState<"edit" | "add">("edit");

  const timelineRows = useMemo<TimelineItem[]>(() => {
    const planRows = milestones
      .filter((m) => m.start_date)
      .map((m) => ({
        trial: planName,
        event_name: m.event_name,
        event_type: m.event_type,
        start_date: m.start_date,
        end_date: m.end_date || m.start_date,
        ongoing_derived: false,
        core_table: "planning",
      }));

    // Roll up site-level startup milestones: one band per category spanning
    // the earliest to the latest date across the plan's included sites.
    const sites = activeSites(plan?.sites ?? []);
    const rollupRows = SITE_MILESTONE_LABELS.flatMap(([key, label]) => {
      const dates = sites
        .map((site) => site.siteStartup?.[key])
        .filter((d): d is string => Boolean(d))
        .sort();
      if (!dates.length) return [];
      return [
        {
          trial: planName,
          event_name: `${label} — all sites (${dates.length})`,
          event_type: "Site Roll-up",
          start_date: dates[0],
          end_date: dates[dates.length - 1],
          ongoing_derived: false,
          core_table: "planning",
        },
      ];
    });

    return [...planRows, ...rollupRows];
  }, [milestones, planName, plan?.sites]);

  const firstEventDate = useMemo(() => {
    if (!timelineRows.length) return null;
    const min = Math.min(
      ...timelineRows.map((r) => new Date(r.start_date).getTime()),
    );
    return new Date(min);
  }, [timelineRows]);

  const lastEventDate = useMemo(() => {
    if (!timelineRows.length) return null;
    const max = Math.max(
      ...timelineRows.map((r) =>
        new Date(r.end_date ?? r.start_date).getTime(),
      ),
    );
    return new Date(max);
  }, [timelineRows]);

  const timelineControls = useTimelineControls({
    firstEventDate,
    initialViewMode: ViewMode.Month,
    initializeToFirstEvent: true,
  });

  if (!plan) return null;

  const openEdit = (milestone: PlanMilestone) => {
    setEditingMilestone(milestone);
    setEditorMode("edit");
    setEditorOpen(true);
  };

  const openAdd = () => {
    setEditingMilestone(null);
    setEditorMode("add");
    setEditorOpen(true);
  };

  const handleSave = (updated: PlanMilestone) => {
    updatePlan(plan.id, {
      milestones: milestones.map((m) => (m.id === updated.id ? updated : m)),
    });
    setEditorOpen(false);
  };

  const handleAdd = (fields: Omit<PlanMilestone, "id">) => {
    updatePlan(plan.id, {
      milestones: [...milestones, { ...fields, id: crypto.randomUUID() }],
    });
    setEditorOpen(false);
  };

  const handleDelete = (id: string) => {
    updatePlan(plan.id, {
      milestones: milestones.filter((m) => m.id !== id),
    });
  };

  const columns: ColumnsType<PlanMilestone> = [
    {
      title: "Milestone",
      dataIndex: "event_name",
      key: "event_name",
      render: (v: string) => (
        <span className="font-medium text-[13px] text-[#1F2933]">{v}</span>
      ),
    },
    {
      title: "Type",
      dataIndex: "event_type",
      key: "event_type",
      render: (v: string) => (
        <span
          className={`inline-flex items-center h-5 rounded-[4px] px-1.5 text-[12px] font-semibold ${
            EVENT_TYPE_BADGE[v] ?? "bg-[#F3F4F6] text-[#6B7280]"
          }`}
        >
          {v}
        </span>
      ),
    },
    {
      title: "Start Date",
      dataIndex: "start_date",
      key: "start_date",
      render: (v: string) =>
        v ? (
          <span className="text-[13px] text-[#374151]">
            {new Date(v + "T12:00:00").toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </span>
        ) : (
          <span className="text-[12px] italic text-[#6B7280]">Not set</span>
        ),
    },
    {
      title: "End Date",
      dataIndex: "end_date",
      key: "end_date",
      render: (v: string) =>
        v ? (
          <span className="text-[13px] text-[#374151]">
            {new Date(v + "T12:00:00").toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </span>
        ) : (
          <span className="text-[12px] italic text-[#6B7280]">—</span>
        ),
    },
    {
      key: "actions",
      width: 80,
      align: "center",
      render: (_, row) => (
        <div className="flex items-center gap-1 justify-center">
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEdit(row)}
            className="text-[#9CA3AF] hover:text-[#0C98D0]"
          />
          <Button
            type="text"
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(row.id)}
            className="text-[#9CA3AF] hover:text-[#EF4444]"
          />
        </div>
      ),
    },
  ];

  return (
    <div>
      {/* Milestone table */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-[14px] font-semibold text-[#1F2933]">Milestones</h2>
        <Button
          size="small"
          type="primary"
          icon={<PlusOutlined />}
          className="bg-[#0C98D0] border-none"
          onClick={openAdd}
        >
          Add Milestone
        </Button>
      </div>

      <div className="rounded-[8px] border border-[#F0F2F4] bg-white shadow-[0_2px_8px_rgba(15,23,42,0.06)] overflow-hidden mb-6">
        <Table<PlanMilestone>
          dataSource={milestones}
          columns={columns}
          rowKey="id"
          pagination={false}
          size="small"
        />
      </div>

      {/* Timeline (only when dates exist) */}
      {timelineRows.length > 0 && (
        <div>
          <h2 className="text-[14px] font-semibold text-[#1F2933] mb-3">
            Timeline
          </h2>
          <div className="rounded-[8px] border border-[#F0F2F4] bg-white shadow-[0_2px_8px_rgba(15,23,42,0.06)] p-4">
            <TimelineHeaderControls
              viewMode={timelineControls.viewMode}
              selectedDate={timelineControls.selectedDate}
              isTodayActive={timelineControls.isTodayActive}
              isFirstEventActive={timelineControls.isFirstEventActive}
              hasFirstEventDate={Boolean(firstEventDate)}
              minSelectableDate={firstEventDate}
              maxSelectableDate={lastEventDate}
              testIdPrefix="planning-timeline"
              onViewModeChange={timelineControls.handleViewModeChange}
              onDateChange={timelineControls.handleDateChange}
              onTodayClick={timelineControls.handleTodayClick}
              onEarliestClick={timelineControls.handleEarliestClick}
            />
            <TimelineLibrary
              data={timelineRows}
              viewMode={timelineControls.viewMode}
              viewDate={timelineControls.viewDate}
              centerOnDate={timelineControls.centerOnDate}
              centerOnDateRequestId={timelineControls.centerOnDateRequestId}
              columns={TIMELINE_COLUMNS}
              utils={milestonesTimelineUtils}
              viewportOffset={220}
              onHorizontalScroll={timelineControls.handleHorizontalScroll}
              onRowSelect={(item) =>
                timelineControls.handleDateChange(dayjs(item.start_date))
              }
            />
          </div>
        </div>
      )}

      {timelineRows.length === 0 && (
        <div className="rounded-[8px] border border-dashed border-[#D1D5DB] bg-white py-8 flex flex-col items-center">
          <p className="text-[13px] text-[#6B7280]">
            Set start dates on milestones above to see the timeline.
          </p>
        </div>
      )}

      <MilestoneEditor
        open={editorOpen}
        milestone={editingMilestone}
        mode={editorMode}
        onClose={() => setEditorOpen(false)}
        onSave={handleSave}
        onAdd={handleAdd}
      />
    </div>
  );
}
